import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  OnInit,
  OnDestroy,
  inject
} from '@angular/core';
import { Subscription } from 'rxjs';
import { IdentityServiceImpl } from 'src/app/infrastructure/adapters/security/identity.service';

/**
 * Directiva estructural que oculta elementos si el usuario no tiene acceso al recurso SEUS.
 * Similar a *ngIf pero basado en permisos del sistema de seguridad.
 *
 * @example
 * ```html
 * <!-- Ocultar botón si no tiene permiso -->
 * <p-button *hasAccess="'btnProgramacionMasivaPS'" label="Programación masiva" />
 *
 * <!-- Ocultar sección completa -->
 * <div *hasAccess="'seccionAdmin'">
 *   <h2>Panel de Administración</h2>
 *   ...contenido solo para admins...
 * </div>
 *
 * <!-- Ocultar opción de menú -->
 * <p-menuitem *hasAccess="'menuReportes'" label="Reportes" />
 * ```
 *
 * @remarks
 * - Si no se especifica recurso, el elemento se muestra (comportamiento permisivo)
 * - En caso de error de red, el elemento se oculta (fail-safe)
 * - La verificación se hace una sola vez al inicializar el componente
 *
 * @see IdentityServiceImpl.isAuthorized
 */
@Directive({
  selector: '[hasAccess]',
  standalone: true
})
export class HasAccessDirective implements OnInit, OnDestroy {

  private readonly identityService = inject(IdentityServiceImpl);
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly viewContainer = inject(ViewContainerRef);

  private subscription?: Subscription;
  private hasView = false;

  /**
   * ID del recurso SEUS a verificar.
   * Debe coincidir con el ID configurado en el sistema SEUS.
   */
  @Input() hasAccess = '';

  ngOnInit(): void {
    if (!this.hasAccess) {
      // Si no se especifica recurso, mostrar el elemento (comportamiento permisivo)
      this.showElement();
      return;
    }

    this.subscription = this.identityService.isAuthorized(this.hasAccess).subscribe({
      next: (hasAccess) => {
        if (hasAccess && !this.hasView) {
          this.showElement();
        } else if (!hasAccess && this.hasView) {
          this.hideElement();
        } else if (!hasAccess && !this.hasView) {
          // No hacer nada, ya está oculto
        }
      },
      error: (err) => {
        console.warn(`[HasAccessDirective] Error verificando acceso a '${this.hasAccess}':`, err);
        // Fail-safe: ocultar en caso de error de red
        this.hideElement();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private showElement(): void {
    if (!this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    }
  }

  private hideElement(): void {
    if (this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}
